import Cookies from "js-cookie";
import axios from "axios";
import {jwtDecode, JwtPayload} from "jwt-decode";

export const ApiBaseUrl: string = process.env.BACKOFFICE_API_BASE_URL ?? 'VACIO';

export const setCookieForOneDay = (key: string, value: any) => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 1);
    Cookies.set(key, value, { expires: expirationDate });
};

// Evita que varias peticiones en paralelo disparen la misma redirección.
let cerrandoSesion = false;

// Sesión inservible: la cookie existe pero la API ya no la acepta (token firmado con
// otra clave, ilegible, o refresh rechazado). Hay que limpiarla y volver al login:
// el layout del dashboard solo comprueba que la cookie *exista*, así que sin esto la
// sesión queda en un limbo en el que todas las vistas fallan con 401 y el usuario
// nunca es enviado de vuelta a iniciar sesión.
const cerrarSesionInvalida = () => {
    if (cerrandoSesion) return;
    // Se borra siempre: el layout de login rebota a /dashboardlayout mientras la cookie
    // exista, así que limpiarla es lo que evita el bucle al llegar a "/".
    Cookies.remove("bo-auth");
    Cookies.remove("bo-username");
    Cookies.remove("bo-password");
    if (typeof window !== "undefined" && window.location.pathname !== "/") {
        // El flag se levanta solo al navegar de verdad; la recarga lo vuelve a poner en
        // false. Si se levantara siempre, un 401 ocurrido en la pantalla de login
        // dejaría sin redirección a todas las sesiones posteriores.
        cerrandoSesion = true;
        window.location.href = "/";
    }
};

const sinAutorizacion = () => ({ headers: {} });

// La API responde 401 tanto cuando la sesión no sirve como cuando el usuario no tiene
// permiso sobre la sección, y hay que distinguirlos: el segundo caso trae `errorMessage`
// en el cuerpo y las vistas lo muestran como aviso, así que no debe cerrar la sesión.
// En las descargas (responseType "blob") el cuerpo del error llega como Blob: hay que leerlo
// para ver si trae `errorMessage`. Se lee sin reemplazarlo (un Blob se puede volver a leer),
// porque varias pantallas lo interpretan por su cuenta.
export const cuerpoJsonDeError = async (data: unknown): Promise<any> => {
    if (!(data instanceof Blob)) return data;
    try {
        return JSON.parse(await data.text());
    } catch {
        return undefined; // no era JSON
    }
};

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status;
        if (status === 401) {
            const cuerpo = await cuerpoJsonDeError(error?.response?.data);
            const esFaltaDePermiso = cuerpo?.errorMessage !== undefined;
            if (!esFaltaDePermiso) cerrarSesionInvalida();
        }
        return Promise.reject(error);
    }
);

export const axiosDefaultConf = async () => {
    const token = Cookies.get("bo-auth") ?? "";
    if (!token) {
        cerrarSesionInvalida();
        return sinAutorizacion();
    }

    let expDate = 0;
    try {
        const decodedToken: JwtPayload = jwtDecode(token);
        expDate = ((decodedToken.exp ?? 0) * 1000);
    } catch {
        // Token ilegible: no tiene sentido intentar refrescarlo.
        cerrarSesionInvalida();
        return sinAutorizacion();
    }

    const currentDate: number = Date.now();
    if (expDate > currentDate) {
        return({
            headers: {
                "Authorization": `Bearer ${token}`,
            }
        })
    }

    try {
        const authResponse = await axios.post(
          `${ApiBaseUrl}backoffice/refreshToken`,
          {
              userToken: token
          }
        );
        setCookieForOneDay("bo-auth", authResponse.data.token);
        return({
            headers: {
                "Authorization": `Bearer ${authResponse.data.token}`,
            }
        });
    } catch (error: any) {
        // axios rechaza toda respuesta no-2xx, así que el 401 del refresh llega como
        // excepción. Solo el 401 es sesión muerta (tope de 12 h, usuario inactivo o
        // borrado). Un 5xx o un error de red es una falla momentánea de la API o la BD:
        // no se cierra la sesión; se lanza para que la vista no haga la petición y
        // muestre su error, y la próxima llamada vuelve a intentar renovar.
        if (error?.response?.status === 401) {
            cerrarSesionInvalida();
            return sinAutorizacion();
        }
        throw error;
    }
};
